/**
 * Resolves markdown links (e.g. "Hades-Data-Dictionary.md") to Content entity IDs.
 * Uses SCIM filter API with in-memory caching to avoid redundant queries.
 */

import { entityService } from './entityServiceSingleton';

export interface ResolvedContentLink {
    id: string;
    displayName: string;
}

const linkCache = new Map<string, ResolvedContentLink | null>();

/**
 * Detect whether an href looks like an inter-Content link.
 * Matches: relative .md links, bare filenames, paths without protocol.
 * Does NOT match: http(s):// URLs, mailto:, #fragment-only, javascript:
 */
export function isContentLink(href: string): boolean {
    if (!href) return false;
    const trimmed = href.trim();
    if (trimmed.startsWith('#')) return false;
    if (trimmed.startsWith('mailto:')) return false;
    if (trimmed.startsWith('javascript:')) return false;
    if (/^https?:\/\//i.test(trimmed)) return false;
    if (trimmed.endsWith('.md')) return true;
    // Bare filename-like links without extension that contain dashes/underscores
    // (common in wiki-style linking) -- only if no path separators or queries
    if (/^[A-Za-z0-9_-]+$/.test(trimmed) && trimmed.length > 2) return true;
    return false;
}

/**
 * Extract the raw filename (without extension or path) from a link target.
 * "Hades-Data-Dictionary.md" -> "Hades-Data-Dictionary"
 */
export function extractRawName(href: string): string {
    let name = href.trim();
    if (name.toLowerCase().endsWith('.md')) {
        name = name.slice(0, -3);
    }
    const lastSlash = name.lastIndexOf('/');
    if (lastSlash >= 0) {
        name = name.slice(lastSlash + 1);
    }
    return name.trim();
}

/**
 * Normalize a link target into a DisplayName search term.
 * "Hades-Data-Dictionary.md" -> "Hades Data Dictionary"
 */
export function normalizeLinkTarget(href: string): string {
    return extractRawName(href).replace(/[-_]+/g, ' ').trim();
}

/**
 * Resolve a markdown link target to a Content entity.
 *
 * Phase 1: tries a cascade of SCIM filter strategies (eq/co on DisplayName and Subject)
 * using both the normalized name ("Hades Data Dictionary") and raw filename ("Hades-Data-Dictionary").
 *
 * Phase 2: falls back to freetext search with word-overlap scoring for fuzzy matches
 * (e.g. "Hades-ERD-Diagrams" → "Hades ERD & Architecture Diagrams").
 *
 * Results are cached in-memory for the browser session.
 */
export async function resolveContentLink(
    linkTarget: string,
    signal?: AbortSignal
): Promise<ResolvedContentLink | null> {
    const normalized = normalizeLinkTarget(linkTarget);
    if (!normalized) return null;

    const cacheKey = normalized.toLowerCase();
    if (linkCache.has(cacheKey)) {
        return linkCache.get(cacheKey) ?? null;
    }

    const rawName = extractRawName(linkTarget);
    const attrs = ['Id', 'DisplayName', 'Subject'];

    const getItems = (result: any): any[] => result?.data || result?.items || [];

    const pickFirst = (items: any[]): ResolvedContentLink | null => {
        if (!items || items.length === 0) return null;
        const r = items[0];
        return {
            id: r.Id || r.id,
            displayName: r.DisplayName || r.displayName || r.Subject || normalized,
        };
    };

    // Phase 1: exact eq/co filter strategies (fast, precise)
    const strategies: string[] = [
        `DisplayName eq "${normalized}"`,
        `Subject eq "${rawName}"`,
        `DisplayName eq "${rawName}"`,
        `Subject co "${normalized}"`,
        `DisplayName co "${normalized}"`,
        `DisplayName co "${rawName}"`,
    ];

    try {
        for (const filter of strategies) {
            const result = await entityService.getEntitiesByType(
                'Content', 1, 5, undefined, filter, attrs, signal
            );
            const match = pickFirst(getItems(result));
            if (match) {
                linkCache.set(cacheKey, match);
                return match;
            }
        }

        // Phase 2: freetext search fallback -- handles cases where link names
        // don't exactly match DisplayName (e.g. "Hades-ERD-Diagrams" vs "Hades ERD & Architecture Diagrams")
        const searchResult = await entityService.getEntitiesByType(
            'Content', 1, 10, undefined, undefined, attrs, signal,
            normalized, 'DisplayName,Subject', undefined, false, true
        );
        const searchItems = getItems(searchResult);

        if (searchItems.length > 0) {
            const words = normalized.toLowerCase().split(/\s+/);
            let bestMatch: any = null;
            let bestScore = 0;

            for (const item of searchItems) {
                const dn = ((item.DisplayName || item.displayName || '') as string).toLowerCase();
                const subj = ((item.Subject || item.subject || '') as string).toLowerCase();
                const combined = `${dn} ${subj}`;
                const score = words.filter((w) => combined.includes(w)).length;
                if (score > bestScore) {
                    bestScore = score;
                    bestMatch = item;
                }
            }

            if (bestMatch && bestScore >= Math.max(1, words.length - 1)) {
                const resolved = pickFirst([bestMatch]);
                if (resolved) {
                    linkCache.set(cacheKey, resolved);
                    return resolved;
                }
            }
        }

        linkCache.set(cacheKey, null);
        return null;
    } catch (err) {
        if (signal?.aborted) return null;
        console.warn('ContentLinkResolver: failed to resolve', linkTarget, err);
        return null;
    }
}

/** Clear the resolution cache (e.g. after Content entity creation/deletion). */
export function clearContentLinkCache(): void {
    linkCache.clear();
}
